"use client";

import { cn } from "utils";
import type { AskUserQuestionToolInfo } from "./types";
import { InteractiveToolCardShell } from "./AskUserQuestionCard";
import type { RequestPortfolioCardLabels } from "./resolveRequestPortfolioLabels";
import {
  REQUEST_PORTFOLIO_ACTION,
  type RequestPortfolioCardAction,
} from "./requestPortfolioAction";
import { trackOnboardingAnalytics } from "@/src/hooks/onboarding/onboardingAnalytics";
import { AccountCategoryCards } from "@components/PlaidConnectFlow/shared/AccountCategoryCards";
import type { PlaidAccountCategory } from "@components/PlaidConnectFlow/types";
import { mapIntentSideToCategory } from "./requestPortfolioIntentSide";

export { type RequestPortfolioCardAction };

export interface RequestPortfolioCardProps {
  labels: RequestPortfolioCardLabels;
  toolInfo?: AskUserQuestionToolInfo;
  className?: string;
  /** 非最后一条消息且无 result 时禁用交互 */
  disabled?: boolean;
  /**
   * `request_portfolio` 工具的 `intentSide`（invest / banking / both）。
   * 缺省或 `both` → 双卡（存量行为）；其余 → 单侧卡。
   */
  intentSide?: string;
  onAction: (action: RequestPortfolioCardAction) => void;
  trackingScene?: string;
}

export const RequestPortfolioCard = ({
  labels,
  toolInfo,
  className,
  disabled = false,
  intentSide,
  onAction,
  trackingScene,
}: RequestPortfolioCardProps) => {
  const shouldTrackOnboarding = trackingScene === "onboarding";

  const handleSelectCategory = (category: PlaidAccountCategory) => {
    if (shouldTrackOnboarding) {
      // GA 双写，OpenReplay 单发 PRD 名：两个选项都进入 Plaid 连接漏斗
      trackOnboardingAnalytics("ob_plaid_clicked", undefined, {
        event: "ob_funnel_plaid_started",
      });
    }

    onAction(
      category === "invest"
        ? REQUEST_PORTFOLIO_ACTION.CONNECT_INVESTMENT
        : REQUEST_PORTFOLIO_ACTION.CONNECT_BANK,
    );
  };

  // 头部样式：Figma `105195:368814`（标题 title_xl 18/26、副标 body_m 14/20
  // text-content_secondary；标题↔副标 8px、头部↔双卡 16px）；外层容器沿用 shell。
  return (
    <InteractiveToolCardShell
      title={null}
      toolInfo={toolInfo}
      className={cn("mb-3", className)}
    >
      <div className="flex flex-col gap-2">
        <h3
          className="m-0 min-w-0 [word-break:break-word] title_xl text-content_primary"
          data-testid="request-portfolio-card-title"
        >
          {labels.title}
        </h3>
        <p
          className="m-0 body_m text-content_secondary"
          data-testid="request-portfolio-card-description"
        >
          {labels.description}
        </p>
      </div>
      <div className="mt-4">
        <AccountCategoryCards
          disabled={disabled}
          onSelectCategory={handleSelectCategory}
          only={mapIntentSideToCategory(intentSide)}
        />
      </div>
    </InteractiveToolCardShell>
  );
};
